"use client";

import { useTranslations } from "next-intl";
import Link from "next/link";
import {
  Paper,
  Text,
  Stack,
  Grid,
  TbTrees,
  TbRuler,
  TbCoinEuro,
  Button,
  Tabs,
  Avatar,
  DataTable,
  Badge,
  Collapsible,
  StatCard,
  TbEmpathize,
  Box,
  TbExternalLink,
} from "@forestsync/ui";
import { TbHomeEco, TbTarget, TbCalendar } from "@forestsync/ui";
import type { FC } from "react";
import type { ProjectDeveloperDetail } from "@/types/glassroom";
import { getOrganizationTypeBadge } from "@forestsync/lib/type-utils";
import { getTransactionTypeDisplayName } from "@forestsync/lib/type-utils";
import { CarbonSinkRow } from "../common/CarbonSinkRow";

interface ProjectDeveloperDetailViewProps {
  user: ProjectDeveloperDetail & { hasBothRoles?: boolean };
}

export const ProjectDeveloperDetailView: FC<
  ProjectDeveloperDetailViewProps
> = ({ user }) => {
  const t = useTranslations();

  // Get organization type badge
  const orgType = getOrganizationTypeBadge(user.type || 0, t);

  // Format date helper
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  // Format transaction type
  const formatTransactionType = (tx: any) =>
    getTransactionTypeDisplayName(tx, user.refId || "", t);

  // Group transactions by year
  const transactionsByYear =
    user.recentTransactions?.reduce(
      (acc, transaction) => {
        const year = new Date(transaction.timestamp).getFullYear().toString();
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(transaction);
        return acc;
      },
      {} as Record<string, typeof user.recentTransactions>,
    ) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <TbHomeEco size={30} className="text-amber-700 dark:text-amber-400" />
          <div>
            <Text
              size="xl"
              component="span"
              fw={700}
              className="text-gray-900 dark:text-white"
            >
              {user.name}{" "}
              <Badge variant="light" color={orgType.color} className="ml-2">
                {orgType.label}
              </Badge>
            </Text>
            <Text size="sm" className="text-gray-500 dark:text-gray-400">
              {[user.municipality, user.country].filter(Boolean).join(", ")}
            </Text>
          </div>
        </div>
        {/* Switch Role Button */}
        {user.hasBothRoles && (
          <Button
            component={Link}
            href={`/beneficiary/${user.refId}`}
            variant="outline"
            color="blue"
            size="md"
            leftSection={<TbEmpathize size={16} />}
          >
            {t("glassroom.viewBeneficiaryProfile")}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t("glassroom.table.projectsOwned")}
            value={user.totalForests ?? 0}
            icon={TbTrees}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t("common.totalHectaresManaged")}
            value={user.totalHectares ?? 0}
            icon={TbRuler}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t("glassroom.table.creditsIssued")}
            value={user.totalCredits ?? 0}
            icon={TbCoinEuro}
            color="yellow"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title={t("glassroom.stats.co2TokensStored")}
            value={user.totalCo2Storage ?? 0}
            icon={TbTarget}
            color="emerald"
          />
        </Grid.Col>
      </Grid>

      {/* Overview and Contact Card Row */}
      <Grid align="stretch">
        {/* Overview */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Paper withBorder p="lg" radius="md" h="100%">
            <Stack gap="md">
              {user.description && (
                <div>
                  <Text
                    size="sm"
                    fw={600}
                    className="text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t("common.description")}
                  </Text>
                  <Text size="sm" className="text-gray-600 dark:text-gray-400">
                    {user.description}
                  </Text>
                </div>
              )}
            </Stack>
          </Paper>
        </Grid.Col>
        {/* Contact Card */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Paper
            withBorder
            p="lg"
            radius="md"
            className="flex flex-col items-center h-full"
          >
            <Avatar
              size={150}
              radius="50%"
              src={user.logoUrl ?? undefined}
              name={user.contactInfo?.name ?? user.name ?? undefined}
              className="mb-3"
              styles={{ image: { objectFit: "contain", padding: 6 } }}
            />
            <Text fw={600} className="mb-1 text-center">
              {user.contactInfo?.name || user.name}
            </Text>
            {user.contactInfo?.email && (
              <Text size="sm" className="text-gray-600 mb-1 text-center">
                {user.contactInfo.email}
              </Text>
            )}
            {user.contactInfo?.website && (
              <Text size="sm" className="text-blue-600 mb-1 text-center">
                {user.contactInfo.website}
              </Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Tabs Section */}
      <Paper withBorder p={{ base: "xs", sm: "lg" }} radius="md">
        <Tabs defaultValue="managedProjects">
          <Tabs.List>
            <Tabs.Tab value="managedProjects">
              {t("glassroom.table.managedProjects")}
            </Tabs.Tab>
            <Tabs.Tab value="transactions">
              {t("glassroom.table.transactionHistory")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="managedProjects" pt="md">
            <div className="space-y-2">
              {user.managedForests?.map((forest) => {
                return <CarbonSinkRow key={forest.refId} sink={forest} />;
              })}
              {(!user.managedForests || user.managedForests.length === 0) && (
                <div className="text-center p-8">
                  <Badge variant="light" color="gray" size="lg">
                    {t("glassroom.table.noData")}
                  </Badge>
                </div>
              )}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="transactions" pt="md">
            <div className="space-y-2">
              {Object.entries(transactionsByYear)
                .sort(([a], [b]) => parseInt(b) - parseInt(a))
                .map(([year, transactions]) => (
                  <Collapsible
                    key={year}
                    label={year}
                    count={transactions.length}
                    icon={<TbCalendar size={18} className="text-blue-500" />}
                    depth={0}
                    color="blue"
                  >
                    <Box pl={{ base: "xs", sm: "md" }}>
                      <DataTable
                        data={transactions.map((transaction) => ({
                          id: transaction.refId, // Use refId as the unique identifier
                          refId: transaction.refId,
                          date: formatDate(transaction.timestamp),
                          type: formatTransactionType(transaction),
                          amount: transaction.totalTokens.toLocaleString(),
                          toUser: transaction.toUserName || "N/A",
                          price: transaction.pricePerTon
                            ? `€${transaction.pricePerTon.toFixed(2)}`
                            : "N/A",
                        }))}
                        columns={[
                          { key: "date", label: t("glassroom.table.date") },
                          { key: "type", label: t("glassroom.table.type") },
                          {
                            key: "amount",
                            label: t("glassroom.table.amount"),
                          },
                          { key: "toUser", label: "To" },
                          { key: "price", label: t("glassroom.table.price") },
                          {
                            key: "actions",
                            label: t("glassroom.table.actions"),
                            render: (value: any, row: any) => (
                              <Button
                                component={Link}
                                href={`/transaction/${row.refId}`}
                                size="xs"
                                variant="light"
                                leftSection={<TbExternalLink size={12} />}
                              >
                                {t("transaction.viewTransaction")}
                              </Button>
                            ),
                          },
                        ]}
                        itemsPerPage={10}
                        compact={true}
                        emptyMessage={t("glassroom.table.noData")}
                      />
                    </Box>
                  </Collapsible>
                ))}
            </div>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </div>
  );
};
